import React, { useMemo, useState } from 'react';

import { Classifier, ExactTagsFilter } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import InputAutocomplete, { AutocompleteOption } from '../../Inputs/Autocomplete';
import {
  formatClassifiersToOptions,
  mapSelectedAutocompleteOptions,
} from '../../Inputs/Autocomplete/autocomplete.utils';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';

interface Props {
  root: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  onChange: (value: ExactTagsFilter) => void;
  defaultValue?: ExactTagsFilter;
  items?: Classifier[];
  active: boolean;
}

const AutocompleteTagsFilterInput: React.FC<Props> = React.memo(
  ({
    onChange,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    root,
    active,
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: unknownSwitchEnabled === undefined ? true : unknownSwitchEnabled,
          value: [],
        }
      : {
          value: [],
        },
    items = [],
  }) => {
    const [state, setState] = useState<ExactTagsFilter>(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options: AutocompleteOption[] = useMemo(() => formatClassifiersToOptions(items), []);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    function getValuesNames(value: string[]) {
      const valuesList = value.map((v) => options.find((o) => o.value === v)?.label);

      return valuesList.join(', ');
    }

    const rs: any[] = [];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }
    if (state.value?.length) {
      rs.push({ label: 'Included:', value: getValuesNames(state.value) });
    }

    return (
      <>
        {active ? (
          <>
            {withUnknownSwitch && (
              <SwitchCompaniesUnknowns
                onChange={(value) => {
                  setState({ ...state, includeEmpty: value });
                }}
                checked={state.includeEmpty}
              />
            )}
            <InputAutocomplete
              defaultValue={mapSelectedAutocompleteOptions(options, state.value)}
              onChange={(excludeValue) => {
                setState({
                  ...state,
                  value: excludeValue.map((option: AutocompleteOption) => option.value),
                });
              }}
              options={options}
            />
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

export default AutocompleteTagsFilterInput;
